import { RefObject, useCallback, useMemo, useRef, useState } from "react";
import { CellRef, Rule, RuleType, TableTotalsSpec } from "../../../interfaces/Rule";
import { Tag } from "../../../interfaces/Tag";
import { generatePrefixedId } from "../../../../utils/helpers/IdHelper";
import {
  addRuleToCustomXml,
  editRuleInCustomXml,
  getRulesFromCustomXml,
} from "../../../../utils/helpers/CustomXmlCoRuleHelper";
import NotificationManager from "../../../../common/notification/NotificationManager";
import { VALIDATION_NOTIFICATION } from "../../../../utils/constants/notification";
import { formatMessage } from "../../../../utils/helpers/LanguageHelper";
import { normalizeRule, validateRule } from "../../../../utils/helpers/RulesHelper";
import { useAppSelector } from "../../../../redux/hooks/hooks";

export interface AddRuleProps {
  tags: Tag[];
  rules: Rule[];
  updateRules?: (rules: Rule[]) => void;
  isShown: boolean;
  onHide: () => void;
  selectedRule?: Rule;
}

export interface AddRuleResult {
  ruleTitle: string;
  setRuleTitle: (title: string) => void;
  ruleScript: string;
  setRuleScript: (script: string) => void;
  ruleType: RuleType;
  setRuleType: (type: RuleType) => void;
  selectedTagIds: string[];
  setSelectedTagIds: (ids: string[]) => void;
  tableTotalsTableId: string | undefined;
  setBoundTable: (tableId: string) => void;
  addends: CellRef[];
  addAddends: (cells: CellRef[]) => void;
  removeAddend: (cell: CellRef) => void;
  replaceAddend: (target: CellRef, newCell: CellRef) => void;
  subtotal: CellRef | undefined;
  setSubtotal: (cell: CellRef) => void;
  clearSubtotal: () => void;
  handleBack: () => void;
  handleSave: () => void;
  resetModalState: () => void;
  isSaveEnabled: boolean;
  ruleTitleError: string;
  ruleScriptError: string;
  showValidationInfo: boolean;
  setShowValidationInfo: (show: boolean) => void;
  validationInfoButtonRef: RefObject<HTMLButtonElement>;
  hasPendingAgentEdit: boolean;
}

export function useAddRuleModal(props: AddRuleProps): AddRuleResult {
  const { rules, updateRules, onHide, selectedRule } = props;
  const [ruleTitle, setRuleTitle] = useState<string>(selectedRule?.title ?? "");
  const [ruleScript, setRuleScript] = useState<string>(selectedRule?.script ?? "");
  const [ruleTypeState, setRuleTypeState] = useState<RuleType>(selectedRule?.type ?? RuleType.CUSTOM);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(selectedRule?.tagIds ?? []);
  const [showValidationInfo, setShowValidationInfo] = useState(false);
  const validationInfoButtonRef = useRef<HTMLButtonElement | null>(null);

  // TABLE_TOTALS state
  const [tableTotalsTableId, setTableTotalsTableId] = useState<string | undefined>(selectedRule?.tableTotals?.tableId);
  const [addends, setAddends] = useState<CellRef[]>(selectedRule?.tableTotals?.addends ?? []);
  const [subtotalValue, setSubtotalValue] = useState<CellRef | undefined>(selectedRule?.tableTotals?.subtotal);

  const stagedRuleEdits = useAppSelector((state) => state.askAiChat.stagedRuleEdits);
  const hasPendingAgentEdit = useMemo(
    () => !!selectedRule && stagedRuleEdits.some((e) => e.targetId === selectedRule.id),
    [selectedRule, stagedRuleEdits]
  );

  const ruleNameAlreadyExists = useMemo(() => {
    if (!ruleTitle.trim()) return false;
    return rules.some((r) => r.title.toLowerCase() === ruleTitle.trim().toLowerCase() && r.id !== selectedRule?.id);
  }, [ruleTitle, rules, selectedRule]);

  const ruleTitleError = useMemo(() => {
    if (ruleNameAlreadyExists) {
      return formatMessage("cof.validation.info.uniquerulename");
    }
    return "";
  }, [ruleNameAlreadyExists]);

  const ruleScriptError = useMemo(() => {
    return validateRule(ruleScript);
  }, [ruleScript]);

  const resetModalState = useCallback(() => {
    setRuleTitle("");
    setRuleScript("");
    setRuleTypeState(RuleType.CUSTOM);
    setSelectedTagIds([]);
    setTableTotalsTableId(undefined);
    setAddends([]);
    setSubtotalValue(undefined);
  }, []);

  const handleBack = useCallback(() => {
    onHide();
    resetModalState();
  }, [onHide, resetModalState]);

  // TABLE_TOTALS setters
  const setBoundTable = useCallback(
    (tableId: string) => {
      setTableTotalsTableId(tableId);
      if (tableId !== tableTotalsTableId) {
        setAddends([]);
        setSubtotalValue(undefined);
      }
    },
    [tableTotalsTableId]
  );

  const addAddends = useCallback((cells: CellRef[]) => {
    setAddends((prev) => {
      const deduped = [...prev];
      for (const cell of cells) {
        if (!deduped.some((a) => a.row === cell.row && a.column === cell.column)) {
          deduped.push(cell);
        }
      }
      return deduped;
    });
  }, []);

  const removeAddend = useCallback((cell: CellRef) => {
    setAddends((prev) => prev.filter((a) => !(a.row === cell.row && a.column === cell.column)));
  }, []);

  const replaceAddend = useCallback((target: CellRef, newCell: CellRef) => {
    setAddends((prev) => {
      const replaced = prev.map((a) => (a.row === target.row && a.column === target.column ? newCell : a));
      const deduped: CellRef[] = [];
      for (const cell of replaced) {
        if (!deduped.some((a) => a.row === cell.row && a.column === cell.column)) {
          deduped.push(cell);
        }
      }
      return deduped;
    });
  }, []);

  const setSubtotal = useCallback((cell: CellRef) => {
    setSubtotalValue(cell);
  }, []);

  const clearSubtotal = useCallback(() => {
    setSubtotalValue(undefined);
  }, []);

  const setRuleType = useCallback(
    (type: RuleType) => {
      if (ruleTypeState === RuleType.TABLE_TOTALS && type !== RuleType.TABLE_TOTALS) {
        setTableTotalsTableId(undefined);
        setAddends([]);
        setSubtotalValue(undefined);
      }
      setRuleTypeState(type);
    },
    [ruleTypeState]
  );

  const handleSave = useCallback(() => {
    const onSave = async () => {
      try {
        let newRule: Rule;
        if (ruleTypeState === RuleType.CONSISTENCY) {
          if (selectedRule) {
            newRule = {
              ...selectedRule,
              type: RuleType.CONSISTENCY,
              title: ruleTitle.trim(),
              script: "",
              tagIds: selectedTagIds,
            };
            await editRuleInCustomXml(newRule);
          } else {
            newRule = {
              id: generatePrefixedId("RULE"),
              type: RuleType.CONSISTENCY,
              title: ruleTitle.trim(),
              version: 1,
              script: "",
              tagIds: selectedTagIds,
            };
            await addRuleToCustomXml(newRule);
          }
        } else if (ruleTypeState === RuleType.TABLE_TOTALS) {
          const tableTotals: TableTotalsSpec = { tableId: tableTotalsTableId, addends, subtotal: subtotalValue };
          if (selectedRule) {
            newRule = {
              ...selectedRule,
              type: RuleType.TABLE_TOTALS,
              title: ruleTitle.trim(),
              script: "",
              tagIds: undefined,
              tableTotals,
            };
            await editRuleInCustomXml(newRule);
          } else {
            newRule = {
              id: generatePrefixedId("RULE"),
              type: RuleType.TABLE_TOTALS,
              title: ruleTitle.trim(),
              version: 1,
              script: "",
              tagIds: undefined,
              tableTotals,
            };
            await addRuleToCustomXml(newRule);
          }
        } else {
          const normalizedScript = normalizeRule(ruleScript);
          if (selectedRule) {
            newRule = {
              ...selectedRule,
              type: RuleType.CUSTOM,
              title: ruleTitle.trim(),
              script: normalizedScript,
              tagIds: undefined,
            };
            await editRuleInCustomXml(newRule);
          } else {
            newRule = {
              id: generatePrefixedId("RULE"),
              type: RuleType.CUSTOM,
              title: ruleTitle.trim(),
              version: 1,
              script: normalizedScript,
              tagIds: undefined,
            };
            await addRuleToCustomXml(newRule);
          }
        }

        // Rileggi le regole aggiornate
        const updatedRules = await getRulesFromCustomXml();
        updateRules?.(updatedRules);

        NotificationManager.addNotification({
          key: VALIDATION_NOTIFICATION,
          message: ruleTitle,
          title: formatMessage(selectedRule ? "cof.validation.title.ruleupdated" : "cof.validation.title.rulecreated"),
          type: "success",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });

        resetModalState();
        onHide();
      } catch (e) {
        console.error("[AddRuleModalHook] Failed to save rule:", e);
        NotificationManager.addNotification({
          key: VALIDATION_NOTIFICATION,
          message: formatMessage("cof.validation.title.createrule.error"),
          title: formatMessage("cof.base.error"),
          type: "error",
          closable: true,
          removeOnClose: true,
          isTemporary: true,
        });
      }
    };
    void onSave();
  }, [
    ruleTitle,
    ruleScript,
    ruleTypeState,
    selectedTagIds,
    tableTotalsTableId,
    addends,
    subtotalValue,
    updateRules,
    resetModalState,
    onHide,
    selectedRule,
  ]);

  const isSaveEnabled = useMemo(() => {
    if (!ruleTitle.trim() || ruleNameAlreadyExists) return false;
    if (ruleTypeState === RuleType.CONSISTENCY) return selectedTagIds.length >= 1;
    if (ruleTypeState === RuleType.TABLE_TOTALS) return addends.length >= 1 && !!subtotalValue;
    return !!ruleScript.trim() && !ruleScriptError;
  }, [
    ruleTitle,
    ruleNameAlreadyExists,
    ruleTypeState,
    selectedTagIds,
    ruleScript,
    ruleScriptError,
    addends,
    subtotalValue,
  ]);

  return {
    ruleTitle,
    setRuleTitle,
    ruleScript,
    setRuleScript,
    ruleType: ruleTypeState,
    setRuleType,
    selectedTagIds,
    setSelectedTagIds,
    tableTotalsTableId,
    setBoundTable,
    addends,
    addAddends,
    removeAddend,
    replaceAddend,
    subtotal: subtotalValue,
    setSubtotal,
    clearSubtotal,
    handleBack,
    handleSave,
    resetModalState,
    isSaveEnabled,
    ruleTitleError,
    ruleScriptError,
    showValidationInfo,
    setShowValidationInfo,
    validationInfoButtonRef,
    hasPendingAgentEdit,
  };
}
